import { CustomerPriceGroupProfitMarginApi } from "src/api/generated/erp/sales/masterData/api/customerPriceGroupProfitMarginApi.ts";
import { CustomerPriceGroupProfitMarginView } from "src/api/generated/erp/db/types/tables/customerPriceGroupProfitMarginView.ts";
import { floatColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { InlineCrudDataGrid } from "src/components/common/dataGrid/crud/InlineCrudDataGrid.tsx";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import i18n from "i18next";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProfitMarginRow {
	profitMarginPercent: number | null | undefined;
	salesPartPriceGroupId: number;
	salesPartPriceGroupIsUsed: boolean;
	salesPartPriceGroupName: string;
}

export interface CustomerPriceGroupProfitMarginsDataGridProps {
	customerPriceGroupId: number;
}

export function CustomerPriceGroupProfitMarginsDataGrid({
	customerPriceGroupId,
}: CustomerPriceGroupProfitMarginsDataGridProps) {
	return (
		<ClientSideDataGridModel<
			CustomerPriceGroupProfitMarginView[],
			CustomerPriceGroupProfitMarginView,
			Record<string, never>
		>
			gridId={"B43F80C13B804457"}
			fetchData={() => CustomerPriceGroupProfitMarginApi.getProfitMargins({ customerPriceGroupId })}
			getRows={(data) => data}
			initialParams={{}}
			getRowId={(row) => row.salesPartPriceGroupId}
			render={({ dataGridProps, refreshData }) => (
				<InlineCrudDataGrid<CustomerPriceGroupProfitMarginView>
					columns={getProfitMarginColumns<CustomerPriceGroupProfitMarginView>()}
					save={{
						type: "enabled",
						action: async ({ items }) => {
							await CustomerPriceGroupProfitMarginApi.saveProfitMargins({
								customerPriceGroupId,
								rows: items,
							});
							await refreshData();
							return items;
						},
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.masterData.customerPriceGroup.profitMargins.grid"),
						data: { customerPriceGroupId },
						rowFields: ["salesPartPriceGroupId", "salesPartPriceGroupName", "profitMarginPercent"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
}

export function getProfitMarginColumns<Row extends ProfitMarginRow>() {
	return [
		textColumn<Row>({
			field: "salesPartPriceGroupName" as Exclude<keyof Row, symbol | number>,
			headerName: i18n.t("sales_part_price_group"),
			width: 240,
		}),
		floatColumn<Row>({
			field: "profitMarginPercent" as Exclude<keyof Row, symbol | number>,
			headerName: i18n.t("profit_margin_percent"),
			width: 140,
			editable: true,
			validate: (value, row) => {
				if (row.salesPartPriceGroupIsUsed && value == null) return i18n.t("required");
				if (value != null && (value < 0 || value >= 100)) return i18n.t("profit_margin_percent_range");
				return undefined;
			},
		}),
	];
}
